import cn from 'classnames';
import { ReactNode, useMemo } from 'react';
import {
  AlexaVisibilityResult,
  AlexaVisibilityRun
} from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { HorizontalBarChart } from '@Components/Charts/HorizontalBarChart/HorizontalBarChart';
import { VerticalBarChart } from '@Components/Charts/VerticalBarChart/VerticalBarChart';
import { BarLabel } from '@Components/Charts/VerticalBarChart/vertical-bar-chart-utils';
import { InfoTooltip } from '@Components/InfoTooltip';
import { getChannelSeriesColor } from '@src/utils/colors';
import { toPrettyNumber } from '@src/utils/performance-utils';
import {
  CHART_DEFINITIONS,
  KPI_DEFINITIONS,
  KPI_NOTE,
  PRODUCT_CHART_DEFINITION,
  SOURCES_CHART_DEFINITION
} from '../alexa-visibility-constants';
import { CHOSEN_BRAND_COLOR, UNKNOWN_BRAND_COLOR } from '../alexa-visibility-colors';
import { useBrandColor } from '../hooks/useBrandColor';
import { ExcelExportButton } from './ExcelExportButton';
import {
  buildCitationSourceProductExportRows,
  buildCitationSourcesExportRows,
  buildProductsExportRows,
  CitationSourceStat,
  getBrandMentionShares,
  getCitationTypeShares,
  getLastRunResults,
  getOverviewKpis,
  getTopCitationSources,
  getTopRecommendedProducts,
  ProductShare,
  ShareItem
} from '../alexa-visibility-utils';

interface QueryOverviewTabProps {
  results: AlexaVisibilityResult[];
  runs: AlexaVisibilityRun[];
  targetBrand: string;
}

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;
const formatRank = (rank: number | undefined): string =>
  rank !== undefined ? `#${rank}` : 'Unranked';

interface KpiCardProps {
  label: string;
  value: string | number;
  suffix?: string;
  definition?: string;
  // The same metric for the most recent run alone; omitted when there is no run to compare to.
  lastRunValue?: string | number;
}

const KpiCard = ({ label, value, suffix, definition, lastRunValue }: KpiCardProps) => (
  <div className='aars-kpi-card'>
    <div className='aars-kpi-label-row'>
      <div className='aars-kpi-label'>{label}</div>
      {definition && <InfoTooltip size={Size.SM}>{definition}</InfoTooltip>}
    </div>
    <div className='aars-kpi-body'>
      <div className='aars-kpi-value'>
        {value}
        {suffix && <span className='aars-kpi-suffix'>{suffix}</span>}
      </div>
      {lastRunValue !== undefined && (
        <div className='aars-kpi-last-run'>{lastRunValue} (last run)</div>
      )}
    </div>
  </div>
);

interface KpiCardsRowProps {
  results: AlexaVisibilityResult[];
  runs: AlexaVisibilityRun[];
  targetBrand: string;
}

const KpiCardsRow = ({ results, runs, targetBrand }: KpiCardsRowProps) => {
  // Big numbers pool every result in view; small grey numbers are the latest finished run alone.
  const { kpis, lastRunKpis } = useMemo(() => {
    const lastRunResults = getLastRunResults(results, runs);
    return {
      kpis: getOverviewKpis(results, targetBrand),
      lastRunKpis: lastRunResults ? getOverviewKpis(lastRunResults, targetBrand) : undefined
    };
  }, [results, runs, targetBrand]);

  return (
    <div className='aars-kpi-row'>
      <KpiCard
        label='Visibility Score'
        value={formatPercent(kpis.visibilityScore)}
        lastRunValue={lastRunKpis && formatPercent(lastRunKpis.visibilityScore)}
        definition={KPI_DEFINITIONS.VISIBILITY_SCORE}
      />
      <KpiCard
        label='Share of Voice'
        value={formatPercent(kpis.shareOfVoice)}
        lastRunValue={lastRunKpis && formatPercent(lastRunKpis.shareOfVoice)}
        definition={KPI_DEFINITIONS.SHARE_OF_VOICE}
      />
      <KpiCard
        label='Typical Rank'
        value={formatRank(kpis.typicalRank)}
        lastRunValue={lastRunKpis && formatRank(lastRunKpis.typicalRank)}
        definition={KPI_DEFINITIONS.TYPICAL_RANK}
      />
      <KpiCard
        label='Total Results'
        value={kpis.totalResults}
        lastRunValue={lastRunKpis?.totalResults}
        definition={KPI_DEFINITIONS.TOTAL_RESULTS}
      />
      <KpiCard
        label='Brands Compared'
        value={kpis.brandsCompared}
        lastRunValue={lastRunKpis?.brandsCompared}
        definition={KPI_DEFINITIONS.BRANDS_COMPARED}
      />
      <KpiCard
        label='Strong Citations'
        value={kpis.strongCitations}
        suffix={`${kpis.strongCitationsPercent.toFixed(0)}% of results`}
        lastRunValue={lastRunKpis?.strongCitations}
        definition={KPI_DEFINITIONS.STRONG_CITATIONS}
      />
    </div>
  );
};

interface ChartCardProps {
  title: string;
  subtitle?: string;
  // Explanation shown in a hover tooltip beside the title.
  definition?: string;
  // Controls shown at the right end of the title row, e.g. an export button.
  actions?: ReactNode;
  // Spans the whole chart grid instead of one column.
  wide?: boolean;
  children: ReactNode;
}

const ChartCard = ({ title, subtitle, definition, actions, wide, children }: ChartCardProps) => (
  <div className={cn('aars-chart-card', { 'aars-chart-card-wide': wide })}>
    <div className='aars-chart-card-header'>
      <div className='aars-chart-card-title'>
        {title}
        {subtitle && <span className='aars-chart-card-subtitle'>({subtitle})</span>}
        {definition && <InfoTooltip size={Size.SM}>{definition}</InfoTooltip>}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

// "1,637" with "73%" beside it (or beneath it when the bar is narrow), above each bar.
const getShareLabel = ({ count, percent }: ShareItem): BarLabel => ({
  value: toPrettyNumber(count),
  detail: `${percent.toFixed(0)}%`
});

// "360 (16%)" at the end of a horizontal bar.
const getShareLabelText = ({ count, percent }: ShareItem): string =>
  `${toPrettyNumber(count)} (${percent.toFixed(0)}%)`;

interface ChartProps {
  results: AlexaVisibilityResult[];
  targetBrand: string;
}

// Every bar here is the chosen brand, so every bar is green.
// Percent = share of all results; a result can carry several types, so bars can sum past 100%.
const CitationTypeShareChart = ({ results, targetBrand }: ChartProps) => {
  const data = useMemo(() => getCitationTypeShares(results), [results]);
  return (
    <ChartCard
      title='Citation type distribution'
      subtitle={`${targetBrand} only`}
      definition={CHART_DEFINITIONS.CITATION_TYPES}
    >
      <VerticalBarChart<ShareItem>
        data={data}
        categoryKey='label'
        valueKey='count'
        valueLabel='Results'
        getBarColor={() => CHOSEN_BRAND_COLOR}
        getBarLabel={getShareLabel}
      />
    </ChartCard>
  );
};

// Includes the target brand so it can be compared with competitors; percent = share of all
// brand mentions across the results, not just of the brands shown.
const BrandMentionShareChart = ({ results, targetBrand }: ChartProps) => {
  const getBrandColor = useBrandColor(results, targetBrand);
  const data = useMemo(() => getBrandMentionShares(results, targetBrand), [results, targetBrand]);
  if (data.length === 0) {
    return <EmptyState title='No brands mentioned yet' />;
  }
  return (
    <ChartCard title='Top competing brands' definition={CHART_DEFINITIONS.TOP_BRANDS}>
      <VerticalBarChart<ShareItem>
        data={data}
        categoryKey='label'
        valueKey='count'
        valueLabel='Mentions'
        getBarColor={row => getBrandColor(row.label)}
        getBarLabel={getShareLabel}
      />
    </ChartCard>
  );
};

const PRODUCT_ROW_HEIGHT = 28;

// Each product takes its brand's color (the chosen brand's are green). The export covers every
// product mentioned, not just the ones charted.
const TopProductsChart = ({ results, targetBrand }: ChartProps) => {
  const getBrandColor = useBrandColor(results, targetBrand);
  const data = useMemo(() => getTopRecommendedProducts(results), [results]);
  if (data.length === 0) {
    return <EmptyState title='No products recommended yet' />;
  }

  return (
    <ChartCard
      wide
      title='Top recommended products'
      subtitle='specific products Alexa recommends most across all queries'
      definition={PRODUCT_CHART_DEFINITION}
      actions={
        <ExcelExportButton
          label='Export All Products (Excel)'
          errorTitle="Couldn't export products"
          fileName={`AlexaVisibility_Products_${targetBrand}`}
          getSheets={() => [{ name: 'Products', data: buildProductsExportRows(results) }]}
        />
      }
    >
      <HorizontalBarChart<ProductShare>
        data={data}
        barKey='count'
        yAxisKey='label'
        yAxisLabel='Recommendations'
        barFillColor={UNKNOWN_BRAND_COLOR}
        getBarColor={row => getBrandColor(row.brand)}
        getBarLabel={getShareLabelText}
        pageLimit={data.length}
        fixedHeight
        height={data.length * PRODUCT_ROW_HEIGHT + 40}
      />
    </ChartCard>
  );
};

const TopCitationSourcesChart = ({ results, targetBrand }: ChartProps) => {
  const data = useMemo(() => getTopCitationSources(results), [results]);
  if (data.length === 0) {
    return <EmptyState title='No citation sources yet' />;
  }

  return (
    <ChartCard
      wide
      title='Top citation sources'
      subtitle='publications & sites Alexa references to support recommendations'
      definition={SOURCES_CHART_DEFINITION}
      actions={
        <ExcelExportButton
          label='Export All Sources (Excel)'
          errorTitle="Couldn't export sources"
          fileName={`AlexaVisibility_CitationSources_${targetBrand}`}
          getSheets={() => [
            { name: 'Sources', data: buildCitationSourcesExportRows(results) },
            { name: 'Source Products', data: buildCitationSourceProductExportRows(results) }
          ]}
        />
      }
    >
      <HorizontalBarChart<CitationSourceStat>
        data={data}
        barKey='citations'
        yAxisKey='source'
        yAxisLabel='Citations'
        barFillColor={getChannelSeriesColor(3)}
        getBarLabel={({ citations }) => toPrettyNumber(citations)}
        pageLimit={data.length}
        fixedHeight
        height={data.length * PRODUCT_ROW_HEIGHT + 40}
      />
    </ChartCard>
  );
};

export const QueryOverviewTab = ({ results, runs, targetBrand }: QueryOverviewTabProps) => {
  if (results.length === 0) {
    return (
      <EmptyState
        title='No results yet'
        subtitle='Run a scrape to see how this brand shows up in Alexa recommendations.'
      />
    );
  }

  return (
    <div className='query-overview-tab'>
      <div className='aars-kpi-note'>{KPI_NOTE}</div>
      <KpiCardsRow results={results} runs={runs} targetBrand={targetBrand} />
      <div className='aars-chart-grid'>
        <CitationTypeShareChart results={results} targetBrand={targetBrand} />
        <BrandMentionShareChart results={results} targetBrand={targetBrand} />
        <TopProductsChart results={results} targetBrand={targetBrand} />
        <TopCitationSourcesChart results={results} targetBrand={targetBrand} />
      </div>
    </div>
  );
};
